import { Color, colorToHex, hexToColor } from '../core/types';
import { GradientDef, GradientKind, GradientTarget, DEFAULT_GRADIENT } from '../tools/gradient';
import { read, storage, write } from './storage';

/**
 * The user's own colours and gradients.
 *
 * Kept beside the templates in local storage and validated the same way: this
 * is user-writable data, so everything read back is checked rather than
 * trusted. Palettes are named groups so a project's colours can be kept
 * together instead of accumulating in one undifferentiated list.
 */

export interface SavedPalette {
  id: string;
  name: string;
  colors: string[];
}

export interface SavedGradient {
  id: string;
  name: string;
  gradient: GradientDef;
}

const PALETTE_KEY = 'palettes';
const GRADIENT_KEY = 'gradients';
const MAX_PALETTES = 60;
const MAX_COLORS = 256;
const MAX_GRADIENTS = 60;

function id(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function str(v: unknown, max: number): string {
  return typeof v === 'string' ? v.slice(0, max) : '';
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/* ------------------------------------------------------------------ *
 * Palettes
 * ------------------------------------------------------------------ */

function sanitizePalette(raw: unknown): SavedPalette | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const p = raw as Record<string, unknown>;
  const colors = Array.isArray(p.colors)
    ? (p.colors as unknown[])
        .filter((c): c is string => typeof c === 'string' && HEX.test(c))
        .slice(0, MAX_COLORS)
    : [];
  const name = str(p.name, 60);
  if (!name) return null;
  return { id: str(p.id, 64) || id('pal'), name, colors };
}

export function listPalettes(): SavedPalette[] {
  try {
    const raw = read(PALETTE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(sanitizePalette).filter((p): p is SavedPalette => p !== null);
  } catch {
    return [];
  }
}

function writePalettes(palettes: SavedPalette[]): boolean {
  if (!storage()) return false;
  return write(PALETTE_KEY, JSON.stringify(palettes.slice(0, MAX_PALETTES)));
}

export function createPalette(name: string, colors: Color[] = []): SavedPalette | null {
  const palette: SavedPalette = {
    id: id('pal'),
    name: name.slice(0, 60) || 'Palette',
    colors: colors.map(colorToHex).filter((c) => HEX.test(c)),
  };
  return writePalettes([...listPalettes(), palette]) ? palette : null;
}

export function renamePalette(paletteId: string, name: string): void {
  writePalettes(
    listPalettes().map((p) => (p.id === paletteId ? { ...p, name: name.slice(0, 60) } : p)),
  );
}

export function removePalette(paletteId: string): void {
  writePalettes(listPalettes().filter((p) => p.id !== paletteId));
}

/** Adds a colour, ignoring one the palette already holds. */
export function addColorToPalette(paletteId: string, color: Color): boolean {
  const hex = colorToHex(color);
  if (!HEX.test(hex)) return false;
  let changed = false;
  const next = listPalettes().map((p) => {
    if (p.id !== paletteId || p.colors.includes(hex)) return p;
    changed = true;
    return { ...p, colors: [...p.colors, hex].slice(0, MAX_COLORS) };
  });
  return changed ? writePalettes(next) : false;
}

export function removeColorFromPalette(paletteId: string, hex: string): void {
  writePalettes(
    listPalettes().map((p) =>
      p.id === paletteId ? { ...p, colors: p.colors.filter((c) => c !== hex) } : p,
    ),
  );
}

/* ------------------------------------------------------------------ *
 * Gradients
 * ------------------------------------------------------------------ */

const KINDS: GradientKind[] = ['horizontal', 'vertical', 'diagonal', 'radial', 'conic'];
const TARGETS: GradientTarget[] = ['fg', 'bg', 'both'];

function sanitizeGradient(raw: unknown): SavedGradient | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const g = raw as Record<string, unknown>;
  const def = g.gradient as Record<string, unknown> | undefined;
  if (typeof def !== 'object' || def === null) return null;

  const stops = Array.isArray(def.stops)
    ? (def.stops as unknown[])
        .filter((s): s is Record<string, unknown> => typeof s === 'object' && s !== null)
        .slice(0, 32)
        .map((s) => ({
          position: Math.max(0, Math.min(1, Number(s.position) || 0)),
          color: (Math.trunc(Number(s.color)) & 0xffffff) >>> 0,
        }))
    : [];
  if (stops.length < 2) return null;

  const name = str(g.name, 60);
  if (!name) return null;

  return {
    id: str(g.id, 64) || id('grad'),
    name,
    gradient: {
      kind: KINDS.includes(def.kind as GradientKind) ? (def.kind as GradientKind) : 'horizontal',
      target: TARGETS.includes(def.target as GradientTarget) ? (def.target as GradientTarget) : 'fg',
      stops,
      offset: Number.isFinite(Number(def.offset)) ? Number(def.offset) : 0,
      mirror: def.mirror === true,
      angle: Number.isFinite(Number(def.angle)) ? Number(def.angle) : 45,
    },
  };
}

export function listSavedGradients(): SavedGradient[] {
  try {
    const raw = read(GRADIENT_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(sanitizeGradient).filter((g): g is SavedGradient => g !== null);
  } catch {
    return [];
  }
}

function writeGradients(gradients: SavedGradient[]): boolean {
  if (!storage()) return false;
  return write(GRADIENT_KEY, JSON.stringify(gradients.slice(0, MAX_GRADIENTS)));
}

export function saveGradient(name: string, gradient: GradientDef): SavedGradient | null {
  const entry: SavedGradient = {
    id: id('grad'),
    name: name.slice(0, 60) || 'Gradient',
    // Stored as a copy: editing the brush afterwards must not rewrite history.
    gradient: { ...gradient, stops: gradient.stops.map((s) => ({ ...s })) },
  };
  return writeGradients([entry, ...listSavedGradients()]) ? entry : null;
}

export function removeGradient(gradientId: string): void {
  writeGradients(listSavedGradients().filter((g) => g.id !== gradientId));
}

/* ------------------------------------------------------------------ *
 * Built-in gradient presets
 * ------------------------------------------------------------------ */

function ramp(kind: GradientKind, target: GradientTarget, ...hex: string[]): GradientDef {
  return {
    kind,
    target,
    stops: hex.map((h, i) => ({
      position: hex.length === 1 ? 0 : i / (hex.length - 1),
      color: hexToColor(h),
    })),
    offset: 0,
    mirror: false,
    angle: 45,
  };
}

/** Starting points that look right in a terminal, not just on screen. */
export const GRADIENT_PRESETS: ReadonlyArray<{ name: string; gradient: GradientDef }> = [
  { name: 'Terminal green', gradient: ramp('horizontal', 'fg', '#0a2a12', '#249142', '#32ff94') },
  { name: 'Icy royal', gradient: ramp('horizontal', 'fg', '#e8f0ff', '#7aa2f7', '#2b4dd6') },
  { name: 'Midnight', gradient: ramp('vertical', 'fg', '#1a1b26', '#414868', '#7aa2f7') },
  { name: 'Silver sky', gradient: ramp('horizontal', 'fg', '#f8f8f2', '#a9b1d6', '#565f89') },
  { name: 'Electric blue', gradient: ramp('horizontal', 'fg', '#7dcfff', '#2f6fe0', '#1a1b6e') },
  { name: 'Sunset', gradient: ramp('horizontal', 'fg', '#ffd166', '#f4795b', '#c2185b') },
  { name: 'Ember', gradient: ramp('vertical', 'fg', '#fde68a', '#f97316', '#7f1d1d') },
  { name: 'Toxic', gradient: ramp('horizontal', 'fg', '#d9f99d', '#4ade80', '#065f46') },
  { name: 'Vapour', gradient: ramp('diagonal', 'fg', '#ff79c6', '#bd93f9', '#8be9fd') },
  { name: 'Mono fade', gradient: ramp('horizontal', 'fg', '#ffffff', '#6b7280', '#0b0c0e') },
  { name: 'Amber CRT', gradient: ramp('vertical', 'fg', '#ffb000', '#7a3e00') },
  { name: 'Radial glow', gradient: ramp('radial', 'fg', '#ffffff', '#3b82f6', '#0a0b0f') },
  { name: 'Rainbow', gradient: ramp('horizontal', 'fg', '#f87171', '#fbbf24', '#4ade80', '#38bdf8', '#a78bfa') },
  { name: 'Deep sea', gradient: ramp('vertical', 'bg', '#06121f', '#0b3a53', '#118ab2') },
];

export function defaultGradient(): GradientDef {
  return { ...DEFAULT_GRADIENT, stops: DEFAULT_GRADIENT.stops.map((s) => ({ ...s })) };
}
